// src/services/pftImportService.js
//
// Reads and updates extracted PFTs (pft_imports, one doc per PFT test) for the PFT
// Uploads page. Only the extraction job creates them and nothing deletes them (rules);
// staff tick review items, correct values, and dismiss / restore. Every change is
// appended to pft_imports/{id}/audit with FIELD NAMES only (no values), fail-soft.
// Grouping into patient cards: pftImportGroups.js.

import {
  addDoc,
  collection,
  deleteField,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { getCollectionPath } from "./firestorePaths";

const importsPath = () => getCollectionPath("pft_imports");

/** Live list of every extracted PFT at this site. Returns the unsubscribe function. */
export const watchPftImports = (onChange, onError) =>
  onSnapshot(
    collection(db, importsPath()),
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError,
  );

/** Append-only history of what staff did to an import. Never throws. */
const audit = async (importId, action, by, extra = {}) => {
  try {
    await addDoc(collection(db, `${importsPath()}/${importId}/audit`), {
      action,
      by: by || "unknown",
      at: serverTimestamp(),
      ...extra,
    });
  } catch (e) {
    console.warn("pft import audit failed", e && e.code);
  }
};

/** Tick (or untick) one review item. field "identity" is ticked on every import given. */
export const setReviewed = async (importIds, field, by, checked) => {
  const batch = writeBatch(db);
  for (const id of importIds) {
    batch.update(doc(db, importsPath(), id), {
      [`reviewed.${field}`]: checked ? { by, at: new Date().toISOString() } : deleteField(),
    });
  }
  await batch.commit();
  await Promise.all(importIds.map((id) => audit(id, checked ? "REVIEW_TICKED" : "REVIEW_UNTICKED", by, { field })));
};

/**
 * Tick (or untick) "Verified" on one soft check of an import (pftSoftChecks.js), saved at
 * once like a review tick. Stored on the check itself (test.softChecks.<key>), so it
 * travels with the PFT date when the patient is moved or merged: a value verified here is
 * already verified in RegX. ISO time, not a server timestamp, because the entry is copied
 * into the patient's PFT list (and a Sheet patient's JSON). Audit: field name only.
 */
export const setSoftCheckVerified = async (importId, key, by, checked) => {
  const at = new Date().toISOString();
  await updateDoc(doc(db, importsPath(), importId), {
    [`test.softChecks.${key}.verifiedBy`]: checked ? by : deleteField(),
    [`test.softChecks.${key}.verifiedAt`]: checked ? at : deleteField(),
  });
  await audit(importId, checked ? "SOFT_CHECK_VERIFIED" : "SOFT_CHECK_UNVERIFIED", by, { field: key });
};

/**
 * Save corrected values on one import's test. `values` = only the changed keys.
 * The extraction's own values are kept in `extractedTest` the first time, so the
 * original reading is never lost.
 */
export const saveTestEdits = async (imp, values, by) => {
  const keys = Object.keys(values);
  if (!keys.length) return;
  const update = { lastEditedBy: by, lastEditedAt: serverTimestamp() };
  if (!imp.extractedTest) update.extractedTest = imp.test || {};
  for (const k of keys) update[`test.${k}`] = values[k];
  await updateDoc(doc(db, importsPath(), imp.id), update);
  await audit(imp.id, "VALUES_EDITED", by, { fields: keys });
};

/**
 * Correct the patient's details on every import of a card (e.g. a misread MRN). The
 * extracted details are kept once in `extractedPatient`. An edited MRN is re-normalised
 * to 9 digits by the caller (mrn.js) and passed in as mrn9 / mrnIssue.
 */
export const savePatientEdits = async (imports, values, by) => {
  const keys = Object.keys(values);
  if (!keys.length || !imports.length) return;
  const batch = writeBatch(db);
  for (const imp of imports) {
    const update = { lastEditedBy: by, lastEditedAt: serverTimestamp() };
    if (!imp.extractedPatient) update.extractedPatient = imp.patient || {};
    for (const k of keys) update[`patient.${k}`] = values[k];
    batch.update(doc(db, importsPath(), imp.id), update);
  }
  await batch.commit();
  await Promise.all(imports.map((imp) => audit(imp.id, "PATIENT_DETAILS_EDITED", by, { fields: keys })));
};

/**
 * "Same patient": a person confirms PDFs that print different names / DOBs are one
 * patient, and picks the name and DOB to use. Stored as `identityChoice` (the printed
 * names stay as extracted) and ticks the "Same patient?" check on each import.
 */
export const setIdentityChoice = async (importIds, choice, by) => {
  const batch = writeBatch(db);
  for (const id of importIds) {
    batch.update(doc(db, importsPath(), id), {
      identityChoice: { name: choice.name || "", dob: choice.dob || "" },
      "reviewed.identity": { by, at: new Date().toISOString() },
    });
  }
  await batch.commit();
  await Promise.all(importIds.map((id) => audit(id, "SAME_PATIENT_CONFIRMED", by)));
};

/**
 * "Not the same patient": put these imports on a card of their own. They get a new
 * `splitGroup`, which the grouping never joins with any other (pftImportGroups.js).
 */
export const separateImports = async (importIds, by) => {
  const group = `split_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
  const batch = writeBatch(db);
  for (const id of importIds) {
    batch.update(doc(db, importsPath(), id), { splitGroup: group, separatedBy: by, separatedAt: serverTimestamp() });
  }
  await batch.commit();
  await Promise.all(importIds.map((id) => audit(id, "SEPARATED_FROM_CARD", by, { splitGroup: group })));
};

/** Dismiss (hide from New, e.g. a test file) or restore imports. Never deletes. */
export const setDismissed = async (importIds, by, dismissed) => {
  const batch = writeBatch(db);
  for (const id of importIds) {
    batch.update(doc(db, importsPath(), id), dismissed
      ? { status: "dismissed", dismissedBy: by, dismissedAt: serverTimestamp() }
      : { status: "new", dismissedBy: deleteField(), dismissedAt: deleteField() });
  }
  await batch.commit();
  await Promise.all(importIds.map((id) => audit(id, dismissed ? "DISMISSED" : "RESTORED", by)));
};

/**
 * Record an export of the filtered list on every exported import, BEFORE the file is
 * downloaded. Unlike the other audit entries this one is NOT fail-soft: if it can't be
 * written, the caller doesn't download (PHI leaves RegX only with a record of it).
 * Field names and counts only, no patient details.
 */
export const recordExport = async (importIds, by, { rows, fields }) => {
  const ids = [...new Set(importIds)];
  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(db);
    for (const id of ids.slice(i, i + 400)) {
      batch.set(doc(collection(db, `${importsPath()}/${id}/audit`)), {
        action: "EXPORTED",
        by: by || "unknown",
        at: serverTimestamp(),
        rows,
        fields,
      });
    }
    await batch.commit();
  }
};
